<template>
    <div class="relationship-graph">
        <div class="visually-hidden">人物关系图</div>
        <div v-if="loading" class="loading-overlay" role="alert" aria-live="polite">
            <div class="loading-spinner" aria-label="加载中"></div>
        </div>
        <div id="network" role="img" aria-label="人物关系网络图" :style="{ height: height, width: width }"></div>
    </div>
</template>

<script>
import service from '@/utils/request';
import { Network } from 'vis-network/standalone';

export default {
    name: "RelationshipGraph",
    props: {
        personId: {
            type: [String, Number],
            default: null
        },
        height: {
            type: String,
            default: '400px',
            validator: function(value) {
                return /^\d+(%|px|vh|rem)$/.test(value);
            }
        },
        width: {
            type: String,
            default: '100%',
            validator: function(value) {
                return /^\d+(%|px|vw|rem)$/.test(value);
            }
        }
    },
    data() {
        return {
            network: null,
            nodesArray: [],
            edgesArray: [],
            loading: false,
            debouncedLoadRelations: null
        }
    },
    constants: {
        COLORS: {
            POSITIVE: '#3498db',
            NEGATIVE: '#e74c3c',
            BACKGROUND: 'white'
        },
        NETWORK_OPTIONS: {
            nodes: {
                size: 35,
                font: {
                    size: 20,
                    face: 'sans-serif'
                },
                borderWidth: 3,
                shadow: false,
                shape: 'circularImage'
            },
            edges: {
                width: 2,
                smooth: false,
                shadow: false,
                color: {
                    inherit: true
                },
                font: {
                    size: 20,
                    inherit: true
                },
                arrows: {
                    to: { enabled: true, scaleFactor: 1 }
                }
            },
            physics: {
                enabled: false
            },
            interaction: {
                dragNodes: false,
                zoomView: true,
                hover: true,
                hoverConnectedEdges: false,
                selectConnectedEdges: false,
                selectable: true,
                selectedNodeStyle: false
            }
        }
    },
    methods: {
        isNegativeRelation(relation) {
            const negativeRelations = [
                "战争对手", "政敌", "敌人", "敌对",
                "斩杀", "处决", 
                "谋害", "追杀",
                "叛将", "诈降", "篡位", "通敌",
                "俘获", "俘虏", "俘获", "胁迫", "劫持"
            ];
            return negativeRelations.includes(relation);
        },

        calculateNodePositions() {
            const centerX = 0;
            const centerY = 0;
            const radius = 300;
            
            // 首先找到并设置中心节点
            const centerNode = this.nodesArray.find(node => node.id === this.personId);
            if (centerNode) {
                centerNode.x = centerX;
                centerNode.y = centerY;
                centerNode.fixed = true;
                centerNode.size = 50;  // 固定中心节点大小
                centerNode.color = {
                    border: '#3498db',
                    background: 'white'
                };
                centerNode.font = {
                    color: '#3498db',
                    size: 20,  // 设置字体大小
                    face: 'sans-serif',
                    vadjust: -10  // 调整y值，使姓名离头像更近
                };
            }

            // 过滤出周围节点（非中心节点）
            const surroundingNodes = this.nodesArray.filter(node => node.id !== this.personId);
            
            // 计算每个节点的位置和颜色
            const angleStep = (2 * Math.PI) / surroundingNodes.length;
            const startAngle = -Math.PI / 2;  // 从12点钟方向开始（-90度）
            
            surroundingNodes.forEach((node, index) => {
                const angle = startAngle + index * angleStep;  // 从startAngle开始计算位置
                node.x = centerX + radius * Math.cos(angle);
                node.y = centerY + radius * Math.sin(angle);
                node.fixed = true;
                
                // 设置节点大小为固定值
                node.size = 35;  // 固定周围节点大小
                
                // 设置字体属性
                node.font = {
                    color: nodeColor,
                    size: 16,  // 调整字体大小
                    face: 'sans-serif',
                    vadjust: -10  // 调整y值，使姓名离头像更近
                };
                
                // 检查与该节点相连的边
                const connectedEdge = this.edgesArray.find(edge => 
                    edge.from === this.personId && edge.to === node.id ||
                    edge.to === this.personId && edge.from === node.id
                );
                
                const nodeColor = connectedEdge && this.isNegativeRelation(connectedEdge.label)
                    ? '#e74c3c'  // 负面关系用红色
                    : '#3498db';  // 其他关系用蓝色
                
                // 设置节点边框和文字颜色
                node.color = {
                    border: nodeColor,
                    background: 'white'
                };
            });
        },

        initNetwork() {
            const container = document.getElementById('network');
            
            // 预处理边的颜色
            this.edgesArray.forEach(edge => {
                if (this.isNegativeRelation(edge.label)) {
                    edge.color = '#e74c3c';  // 较柔和的红色
                    edge.font = { color: '#e74c3c' };
                } else {
                    edge.color = '#3498db';  // 较柔和的蓝色
                    edge.font = { color: '#3498db' };
                }
            });
            
            const data = {
                nodes: this.nodesArray,
                edges: this.edgesArray
            };

            const options = {
                nodes: {
                    size: 35,
                    font: {
                        size: 20,
                        face: 'sans-serif'
                    },
                    borderWidth: 3,
                    shadow: false,
                    shape: 'circularImage',
                    chosen: false
                },
                edges: {
                    width: 2,
                    smooth: false,
                    shadow: false,
                    color: {
                        inherit: 'from'
                    },
                    font: {
                        size: 20,
                        color: '#666666'
                    },
                    arrows: {
                        to: { enabled: true, scaleFactor: 1 }
                    }
                },
                physics: {
                    enabled: false
                },
                interaction: {
                    dragNodes: false,
                    dragView: false,
                    zoomView: false,
                    hover: true,
                    hoverConnectedEdges: false,
                    selectConnectedEdges: false,
                    selectable: true
                }
            };

            this.network = new Network(container, data, options);
            
            // 只调整位置，不设置scale
            this.network.moveTo({
                offset: {
                    x: 0,
                    y: -10  // 减小向上偏移的距离
                }
            });

            this.network.on('click', async (params) => {
                if (params.nodes.length > 0) {
                    const nodeId = params.nodes[0];
                    const clickedNode = this.nodesArray.find(node => node.id === nodeId);
                    if (clickedNode && clickedNode.id && clickedNode.id !== this.personId) {  // 添加判断，避免点击当前人物
                        try {
                            const response = await service.get(`/wikiapi/relations/recordId/${clickedNode.id}`);
                            const recordId = response.data;
                            this.$router.push(`/record/${recordId}`);
                        } catch (error) {
                            console.error('获取条目ID失败:', error);
                        }
                    }
                } else if (params.edges.length > 0) {
                    const edgeId = params.edges[0];
                    const clickedEdge = this.edgesArray.find(edge => edge.id === edgeId);
                    if (clickedEdge && clickedEdge.story) {
                        try {
                            const response = await service.get(`/wikiapi/relations/recordId/${clickedEdge.story}`);
                            const recordId = response.data;
                            if (recordId !== this.personId) {  // 添加判断，避免跳转到当前人物
                                this.$router.push(`/record/${recordId}`);
                            }
                        } catch (error) {
                            console.error('获取条目ID失败:', error);
                        }
                    }
                }
            });

            // 添加鼠标悬停效果
            this.network.on('hoverNode', (params) => {
                const nodeId = params.node;
                const node = this.nodesArray.find(n => n.id === nodeId);
                if (node) {
                    this.network.canvas.body.container.style.cursor = 'pointer';
                }
            });

            this.network.on('hoverEdge', (params) => {  // 新增：边的悬停效果
                const edgeId = params.edge;
                const edge = this.edgesArray.find(e => e.id === edgeId);
                if (edge && edge.story) {
                    this.network.canvas.body.container.style.cursor = 'pointer';
                }
            });

            this.network.on('blurNode', () => {
                this.network.canvas.body.container.style.cursor = 'default';
            });

            this.network.on('blurEdge', () => {  // 新增：边的悬停结束效果
                this.network.canvas.body.container.style.cursor = 'default';
            });
        },

        handleError(error, message) {
            console.error(message, error);
            this.$message.error(message);
        },

        async loadPersonRelations() {
            if(!this.personId){
                return;
            }
            try {
                this.loading = true;
                const response = await service.get(`/wikiapi/relations/record/${this.personId}`);
                if (!response.data) {
                    throw new Error('No data received');
                }
                if (response.data.nodes && response.data.edges) {
                    this.nodesArray = response.data.nodes;
                    this.edgesArray = response.data.edges;
                    this.calculateNodePositions();
                    this.initNetwork();
                }
            } catch (error) {
                this.handleError(error, '加载关系图失败，请稍后重试');
                this.nodesArray = [];
                this.edgesArray = [];
            } finally {
                this.loading = false;
            }
        },
        debounce(fn, delay) {
            let timer = null;
            const debounced = (...args) => {
                if (timer) clearTimeout(timer);
                timer = setTimeout(() => fn.call(this, ...args), delay);
            }
            return debounced;
        },
        getNodeColorConfig(isCenter, relation) {
            const color = relation && this.isNegativeRelation(relation)
                ? this.constants.COLORS.NEGATIVE
                : this.constants.COLORS.POSITIVE;
            
            return {
                color: {
                    border: color,
                    background: this.constants.COLORS.BACKGROUND
                },
                font: { color }
            };
        }
    },
    watch: {
        personId: {
            handler: function(newVal) {
                if (this.debouncedLoadRelations) {
                    this.debouncedLoadRelations(newVal);
                }
            },
            immediate: true
        }
    },
    beforeDestroy() {
        if (this.network) {
            this.network.destroy();
            this.network = null;
        }
    },
    created() {
        this.debouncedLoadRelations = this.debounce(this.loadPersonRelations, 300).bind(this);
    }
};
</script>

<style scoped>
.relationship-graph {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
}

#network {
    border: 1px solid #ddd;
    background-color: #f8f9fa;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}
</style>